import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import {
    CButton, CCard, CCardBody, CCardHeader, CForm, CFormInput, CFormSelect, CFormCheck, CCardFooter, CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell, CFormFeedback, CSpinner
} from '@coreui/react';

import { apiClient } from 'services/Api';
import DateInput from 'components/forms/DateInput';
import { jwtDecode } from 'jwt-decode';
import TextEditor from "components/forms/TextEditor";
import ToastNotification from "components/globals/ToastNotification";
import { triggerAlert } from 'components/globals/AlertNotification';
import UserSelectionModal from "components/UserSelectionModal";

const InterfaceSurveillance = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(null);
    const location = useLocation();
    const [toast, setToast] = useState(null);
    const [isEditing, setIsEditing] = useState(true);
    const [fieldErrors, setFieldErrors] = useState({});
    const [permissions, setPermissions] = useState([]);
    const [showModalResponsible, setShowModalResponsible] = useState(false);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false); // State to manage loading

    const token = localStorage.getItem("access_token");
    const decodedToken = jwtDecode(token);
    const is_director = decodedToken.is_director;
    const user_id = decodedToken.user_id;

    const fetchInterfaceSurveillanceDetails = async () => {
        let data = null; // Declare a variable to store API response
        try {
            const response = await apiClient.get(`/interface-surveillances/${id}/`);
            data = response.data.surveillance; // Store response data
            const perms = response.data.permissions;
            setFormData(data); // Update state
            setPermissions(perms); // Update permissions
            setIsEditing(perms?.can_create || perms?.can_update);
        } catch (error) {
            triggerAlert('Vigilância não foi encontrada', 'error');
            navigate("/vigilancia-interfaces");
        }
    };

    const handleCheckboxUsersChangeResponsibles = (userId, isChecked) => {
        setFormData((prevData) => {
           let updatedMembers = isChecked
              ? [...prevData.responsible_members, userId]
              : prevData.responsible_members.filter((id) => id !== userId);
  
           return { ...prevData, responsible_members: updatedMembers };
        });
    };

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                let response;
                if (formData !== null && formData.approved != null && formData.responsible_members.length > 0) {
                    response = await apiClient.get('/users/by-ids/', {
                        params: { ids: [...formData.responsible_members] },
                    });
                } else {
                    response = await apiClient.get('/users/active/');
                }
                setUsers(response.data);
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };

        if (!id) {
            const queryParams = new URLSearchParams(location.search);
            const interfaceParam = queryParams.get("interface");
            setIsEditing(true);
            setFormData({ responsible_members: [], year: new Date().getFullYear(), interface: interfaceParam ?? '' });
        } else {
            fetchInterfaceSurveillanceDetails();
        }

        fetchUsers();
    }, [id, location.pathname]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
    
        let updatedValue;
        if (type === "checkbox") {
            updatedValue = checked;
        } else if (value === "true") {
            updatedValue = true;
        } else if (value === "false") {
            updatedValue = false;
        } else if (value === "") {
            updatedValue = null;
        } else {
            updatedValue = value;
        }
    
        setFormData((prevFormData) => {
            const updatedFormData = {
                ...prevFormData,
                [name]: updatedValue,
            };
    
            // Automatically set objectives when periodicity changes
            if (name === "periodicity") {
                let metas = null;
                switch (updatedValue) {
                    case "Em Continuo":
                        metas = 365;
                        break;
                    case "Semanal":
                        metas = 52;
                        break;
                    case "Mensal":
                        metas = 12;
                        break;
                    case "Semestral":
                        metas = 2;
                        break;
                    case "Trimestral":
                        metas = 4;
                        break;
                    case "Anual":
                        metas = 1;
                        break;
                    default:
                        metas = null;
                }
                updatedFormData.objectives = metas;
            }
    
            return updatedFormData;
        });
    
        setFieldErrors((prevErrors) => {
            const updated = { ...prevErrors };
            delete updated[name];
            return updated;
        });
    
        setIsEditing(true);
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true); // Set loading state to true
        try {
            if (id) {
                await apiClient.put(`/interface-surveillances/${id}/`, formData);
                triggerAlert('Vigilância atualizada com sucesso!', 'success');
                navigate(`/vigilancia-interfaces?interface=${formData.interface}`);
            } else {
                await apiClient.post('/interface-surveillances/', formData);
                triggerAlert('Vigilância atualizada com sucesso!', 'success');
                navigate(`/vigilancia-interfaces?interface=${formData.interface}`);
            }
        } catch (error) {
            const errors = error.response?.data;
            let errorMessage = 'Erro ao gravar';

            if (errors && typeof errors === 'object') {
                setFieldErrors(errors); // <-- Save the full error object
            } else {
                setFieldErrors({});
            }

            setTimeout(() => {
                const firstInvalidInput = document.querySelector('input.is-invalid, textarea.is-invalid, select.is-invalid');
                if (firstInvalidInput) {
                    firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    firstInvalidInput.focus();
                }
            }, 50);

            setToast({ title: 'Vigilância', message: errorMessage, type: 'error' });
            console.log('Error submitting form:', error);

            setLoading(false); // Reset loading state on error
        }
    };

    const getSortedHistory = () => {
        const history = Array.isArray(formData?.update_history) ? formData.update_history : [];
        return [...history].sort((a, b) => {
            const dateA = new Date(a.changed_at || a.changed_on || 0).getTime();
            const dateB = new Date(b.changed_at || b.changed_on || 0).getTime();
            return dateB - dateA;
        });
    };

    const formatHistoryDate = (entry) => {
        const rawDate = entry.changed_at || entry.changed_on;
        if (!rawDate) {
            return "data desconhecida";
        }

        const date = new Date(rawDate);
        if (Number.isNaN(date.getTime())) {
            return rawDate;
        }

        return date.toLocaleString("pt-PT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const renderUpdateHistory = () => {
        if (!id) {
            return null;
        }

        const history = getSortedHistory();

        return (
            <CCard className="mt-3">
                <CCardHeader>
                    <h5 className="mb-0">Histórico de alterações</h5>
                </CCardHeader>
                <CCardBody>
                    {history.length === 0 ? (
                        <p className="text-muted mb-0">Sem alterações registadas.</p>
                    ) : (
                        <ul className="mb-0">
                            {history.map((entry, index) => (
                                <li key={`${entry.changed_at || entry.changed_on || "entry"}-${index}`} className="mb-2">
                                    <div>
                                        No dia {formatHistoryDate(entry)}: {entry.message || "Alteração registada."}
                                    </div>
                                    {entry.changed_by?.name && (
                                        <div className="small text-muted">Por {entry.changed_by.name}</div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </CCardBody>
            </CCard>
        );
    };


    if (formData !== null) {
        if (!isEditing) {
            const renderText = (text) => {
                return <div dangerouslySetInnerHTML={{ __html: text || "-" }} />;
            };

            return (
                <div className="container mt-4">
                    <h4 className="text-center mb-5">Vigilância</h4>
                    {[
                        { label: "Interface", value: formData.interface },
                        { label: "Ano", value: formData.year },
                        { label: "Área", value: formData.area },
                        ...(formData.interface === "Requisitos legais e regulamentação"
                            ? [{ label: "Tema", value: formData.theme }]
                            : []),
                        { label: "Tópicos", value: renderText(formData.topics) },
                        ...(formData.interface === "Requisitos legais e regulamentação"
                            ? [{ label: "Fonte", value: renderText(formData.sources) }]
                            : []),
                        { label: "Periodicidade", value: formData.periodicity },
                        { label: "Metas/Ano", value: formData.objectives },
                        {
                            label: "Responsáveis",
                            value: formData.responsible_members.length > 0 ? (
                                <ul>
                                    {users.filter((user) => formData.responsible_members.includes(user.id)).map((user) => (
                                        <li key={user.id}>{`${user.first_name} ${user.last_name}`}</li>
                                    ))}
                                </ul>
                            ) : (
                               <p className="text-muted">Nenhum responsável selecionado</p>
                            )
                        }
                    ].map((item, index) => (
                        <CCard key={index} className="mb-3">
                            <CCardHeader>
                                <h5>{item.label}</h5>
                            </CCardHeader>
                            <CCardBody>
                                <div>{item.value}</div>
                            </CCardBody>
                        </CCard>
                    ))}
                    {renderUpdateHistory()}

                    {/* Back button */}
                    <div className="d-flex justify-content-end gap-2 mt-3 mb-3">
                        <CButton color="danger" className="text-white" onClick={() => navigate(`/vigilancia-interfaces?interface=${formData.interface}`)}>
                            Voltar
                        </CButton>
                    </div>
                </div>
            );
        }

        return (
            <div className="container mt-4">
                {/* Error Toast - Appears on submit failure */}
                <ToastNotification toast={toast} setToast={setToast} />

                <CCard>
                    <CCardHeader>
                        <h4>Vigilância</h4>
                    </CCardHeader>
                    <CCardBody>
                        <CForm onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <CFormSelect
                                    label={<strong>Interface</strong>}
                                    name="interface"
                                    value={formData.interface ?? ''}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                >
                                    <option value="">Selecione</option>
                                    <option value="Interface de Mercado">Interface de Mercado</option>
                                    <option value="Interface Organizacional">Interface Organizacional</option>
                                    <option value="Interface Tecnológica">Interface Tecnológica</option>
                                    <option value="Requisitos legais e regulamentação">Requisitos legais e regulamentação</option>
                                </CFormSelect>
                                {fieldErrors.interface && (
                                    <CFormFeedback invalid>{fieldErrors.interface[0]}</CFormFeedback>
                                )}
                            </div>

                            <CFormInput
                                type="number"
                                label={<strong>Ano</strong>}
                                name="year"
                                value={formData.year ?? ''}
                                onChange={handleInputChange}
                                readOnly={true}
                                className="mb-3"
                            />

                            <div className="mb-3">
                                <CFormInput
                                    type="text"
                                    label={<strong>Área</strong>}
                                    name="area"
                                    value={formData.area ?? ''}
                                    onChange={handleInputChange}
                                    invalid={!!fieldErrors.area}
                                />
                                {fieldErrors.area && (
                                    <CFormFeedback invalid>{fieldErrors.area[0]}</CFormFeedback>
                                )}
                            </div>

                            {formData.interface === "Requisitos legais e regulamentação" && (
                                <CFormInput
                                    type="text"
                                    label={<strong>Tema</strong>}
                                    name="theme"
                                    value={formData.theme ?? ''}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                />
                            )}

                            <TextEditor
                                label="Tópicos relevantes"
                                value={formData.topics ?? ''}
                                onChange={(value) =>
                                    handleInputChange({ target: { name: "topics", value } })
                                }
                                className="mb-3"
                            />

                            {formData.interface === "Requisitos legais e regulamentação" && (
                                <TextEditor
                                    label="Fontes"
                                    value={formData.sources ?? ''}
                                    onChange={(value) =>
                                        handleInputChange({ target: { name: "sources", value } })
                                    }
                                    className="mb-3"
                                />
                            )}

                            <div className="d-flex justify-content-between mt-3">
                                <div className="w-50 pe-2">
                                    <CButton color="info" className="text-white mb-2" onClick={() => setShowModalResponsible(true)}>
                                        Selecionar Responsáveis
                                    </CButton>
                                    {formData.responsible_members.length > 0 ? (
                                        <ul>
                                            {users
                                            .filter((user) => formData.responsible_members.includes(user.id))
                                            .map((user) => (
                                                <li key={user.id}>{`${user.first_name} ${user.last_name}`}</li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-muted">Nenhum responsável selecionado</p>
                                    )}
                                </div>
                            </div>

                            <CFormSelect
                                label={<strong>Periodicidade</strong>}
                                name="periodicity"
                                value={formData.periodicity ?? ''}
                                onChange={handleInputChange}
                                className="mb-3"
                            >
                                <option value="">Selecione</option>
                                <option value="Em Continuo">Em Continuo</option>
                                <option value="Semanal">Semanal</option>
                                <option value="Mensal">Mensal</option>
                                <option value="Semestral">Semestral</option>
                                <option value="Trimestral">Trimestral</option>
                                <option value="Anual">Anual</option>
                            </CFormSelect>

                            <CFormInput
                                type="number"
                                label={<strong>Metas/Ano</strong>}
                                name="objectives"
                                value={formData.objectives ?? ''}
                                readOnly={true}
                            />

                            <UserSelectionModal
                                title="Selecionar Responsáveis"
                                visible={showModalResponsible}
                                onClose={() => setShowModalResponsible(false)}
                                users={users}
                                selectedUsers={formData.responsible_members}
                                onChange={handleCheckboxUsersChangeResponsibles}
                            />

                        </CForm>
                        {renderUpdateHistory()}
                    </CCardBody>
                    <CCardFooter className="d-flex justify-content-end gap-2">
                        <CButton color="danger" className="text-white" onClick={() => navigate(formData.interface ? `/vigilancia-interfaces?interface=${formData.interface}` : '/vigilancia-interfaces')}>
                            Cancelar
                        </CButton>
                        <CButton disabled={loading} color="success" className="text-white" type="submit" onClick={handleSubmit}>
                            {loading ? <CSpinner size="sm" /> : 'Confirmar'}
                        </CButton>
                    </CCardFooter>
                </CCard>
            </div>
        );
    }

    return (
        <div className="container mt-4">
            <h2>Loading...</h2>
        </div>
    );
};

export default InterfaceSurveillance;
